
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Services list - même liste que dans Register.tsx
const mucodecServices = ["Direction DIDISI", "DOAG", "DCMG", "DAI", "DRCR", "General"];

const Auth = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [service, setService] = useState("General");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Vérifier si l'email est valide
      if (!email.includes('@') || !email.includes('.')) {
        toast.error("Veuillez entrer une adresse email valide");
        setLoading(false);
        return;
      }

      // Vérifier si le mot de passe est assez long
      if (password.length < 6) {
        toast.error("Le mot de passe doit contenir au moins 6 caractères");
        setLoading(false);
        return;
      }

      // Nettoyer les données d'authentification existantes pour éviter les conflits
      localStorage.removeItem('supabase.auth.token');
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Tenter une déconnexion globale
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continuer même si l'erreur se produit
      }

      // Inscription avec Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            first_name: firstName,
            last_name: lastName,
            service: service
          }
        }
      });
      
      if (authError) {
        // Gérer spécifiquement l'erreur de limite d'emails
        if (authError.message?.includes("rate limit exceeded")) {
          toast.error("Limite d'envoi d'emails atteinte. Veuillez réessayer plus tard ou contacter l'administrateur.");
        } else {
          throw authError;
        }
        setLoading(false);
        return;
      }
      
      // Création du profil utilisateur dans user_profiles en utilisant la nouvelle fonction sécurisée
      if (authData.user) {
        try {
          const { error: profileError } = await supabase.rpc('create_user_profile_safe', {
            p_user_id: authData.user.id,
            p_email: email,
            p_first_name: firstName,
            p_last_name: lastName,
            p_service: service,
            p_role: 'user',
            p_is_active: true
          });
          
          if (profileError) {
            console.error("Erreur lors de la création du profil:", profileError);
            // On continue malgré l'erreur pour permettre à l'utilisateur de s'inscrire
          }
        } catch (profileError) {
          console.error("Erreur lors de la création du profil:", profileError);
          // On continue malgré l'erreur
        }
      }
      
      toast.success("Inscription réussie! Veuillez vérifier votre email pour confirmer votre compte.");
      
      // Rediriger vers la page principale après inscription
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de l'inscription");
      console.error("Erreur d'inscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToMainPage = () => {
    navigate("/");
  };

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center text-white px-4"
        style={{ backgroundColor: "rgba(58, 43, 102, 1)" }}
      >
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-rose-600">Mucodec Alerte licence</h1>
            <p className="">Gérez vos licences de produits</p>
          </div>
          <br></br>
          <img
            src="/lovable-uploads/c5e9bf5c-1418-4e99-b5e6-e0a55f0aede9.png"
            alt="Mucodec Logo"
            className="h-24 w-auto mx-auto transition-transform duration-300 group-hover:scale-110 border-2 border-white rounded-lg shadow-md"
          />
          <br></br>
          <Card>
            <CardHeader>
              <CardTitle>Inscription pour E-mail Send</CardTitle>
              <CardDescription>
                Inscrivez vous pour recevoir les alertes mail
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              <form onSubmit={handleSignUp} className="space-y-4">
                {/* Prénom et Nom */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Service/Département */}
                <div className="space-y-2">
                  <Label htmlFor="service">Service/Département</Label>
                  <Select value={service} onValueChange={setService}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez votre service" />
                    </SelectTrigger>
                    <SelectContent>
                      {mucodecServices.map((svc) => (
                        <SelectItem key={svc} value={svc}>
                          {svc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* Mot de passe */}
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Minimum 6 caractères"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum 6 caractères alphanumériques
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Inscription en cours..." : "S'inscrire"}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4 pt-4">
              <Button variant="ghost" className="w-full" onClick={goToMainPage}>
                Continuer sans se connecter
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Auth;
